import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from "react-router";
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';

// Design System Components
import Tab from 'Components/DesignSystem/Tab/Tab';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Button from 'Components/DesignSystem/Button/Button';
import { ArrowLeft } from 'Components/DesignSystem/Iconography/Icons';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';

// Shared Components
import UnsavedChangesModal from 'Components/Common/UnsavedChangesModal';

// View Components
import {
  SKUMatchedView,
  MPNUPCMatchedView,
  UnmatchedView,
  IgnoredProductsView,
  IgnoredUpdatesView,
  ProcessedView,
  MissingMappingView,
} from './views';

// API Services
import { getDataReviewStats, getIncompleteMappingCount } from 'api/DataReview';

// Review Queue (tickets 11446 + 11447)
import { ReviewQueueDrawer, useReviewQueue } from './components/review-queue';

// Utilities
import { transformTabCounts, transformSubTabCounts } from './utils';

// Types and Constants
import { TabItem } from 'types/DesignSystem/Tab';
import { TabId, TabCounts } from './types';
import { TAB_CONFIG, DEFAULT_TAB_COUNTS, PAGE_TITLE } from './constants';

/**
 * Data Review Page - Catalog Management
 *
 * Handles product imports, updates, and cross-channel synchronization.
 * - SKU Matched: Products matched by SKU at Environment level
 * - MPN/UPC Matched: Products matched by MPN/UPC at Industry level
 * - Unmatched: Imported products with no catalog match
 * - Ignored Products: Products marked to be ignored
 * - Ignored Updates: Attribute updates marked to be ignored
 * - Processed: Successfully processed imports
 */
const CatalogManagement = () => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const channelKey = appContext?.channelKey;
  const readPermission = appContext?.pm?.checkPermission("gmscatalogue_read");
  const updatePermission = appContext?.pm?.checkPermission("gmscatalogue_update");
  // Dedicated permission for the whole-tab "Update All" / "Create All"
  // buttons (ticket 13765) — granted to Compatio Admin roles only. The
  // buttons are hidden (not disabled) without it.
  const bulkUpdatePermission = appContext?.pm?.checkPermission("gmscatalogue_bulkupdate");

  const [activeTabId, setActiveTabId] = useState<TabId>('sku-matched');
  const [showMissingMapping, setShowMissingMapping] = useState<boolean>(false);
  const [tabCounts, setTabCounts] = useState<TabCounts>(DEFAULT_TAB_COUNTS);
  const [subTabCounts, setSubTabCounts] = useState<{ unmappedSku: number; unmappedSkuWithUpdates: number }>({
    unmappedSku: 0,
    unmappedSkuWithUpdates: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [reviewQueueOpen, setReviewQueueOpen] = useState<boolean>(false);

  // Review Queue state + actions (tickets 11446 + 11447). Passing
  // `reviewQueueOpen` lets the hook switch to a faster polling cadence
  // while the drawer is open.
  const reviewQueue = useReviewQueue(channelKey, reviewQueueOpen);

  // Unsaved changes modal state for tab switching
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingTabId, setPendingTabId] = useState<TabId | null>(null);
  const [pendingMissingMapping, setPendingMissingMapping] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);

  // Refs to hold the current view's save/cancel handlers
  const editSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const editCancelRef = useRef<(() => void) | null>(null);

  // Callback passed to views so they can register their save/cancel handlers
  const onRegisterEditHandlers = useCallback((handlers: { save: () => Promise<boolean>; cancel: () => void } | null) => {
    if (handlers) {
      editSaveRef.current = handlers.save;
      editCancelRef.current = handlers.cancel;
    } else {
      editSaveRef.current = null;
      editCancelRef.current = null;
    }
  }, []);

  // Sync edit mode with AppContext to block sidebar and workspace selectors
  // Note: When entering edit mode, handlers are registered via onRegisterEditHandlers
  // (called after onEditModeChange), so we use an effect to sync to AppContext
  const handleEditModeChange = useCallback((editing: boolean) => {
    setIsEditMode(editing);
    appContext?.setEditInProgress(editing);
    if (!editing) {
      appContext?.setEditSaveHandler(null);
      appContext?.setEditCancelHandler(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register AppContext handlers whenever edit mode + refs are both available
  useEffect(() => {
    if (isEditMode && editSaveRef.current && editCancelRef.current) {
      const saveRef = editSaveRef;
      const cancelRef = editCancelRef;
      appContext?.setEditSaveHandler(() => () => saveRef.current ? saveRef.current() : Promise.resolve(false));
      appContext?.setEditCancelHandler(() => () => cancelRef.current?.());
    }
  }, [isEditMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Fetch tab counts and missing mapping count from API
   */
  const fetchStats = useCallback(() => {
    if (!channelKey) return;

    setIsLoadingStats(true);

    // Fire the two calls independently rather than awaiting both together.
    // The six tab badges come entirely from /stats; only the Missing Mapping
    // badge needs /incomplete-mapping-count. Previously a Promise.all + a single
    // isLoadingStats flag coupled them, so the tab counts stayed in their loading
    // state until the (often slower) mapping count returned — even though /stats
    // had already resolved. Now the tab badges render the moment /stats returns,
    // and the Missing Mapping badge fills in on its own when its count arrives.
    getDataReviewStats(channelKey)
      .then((statsResponse) => {
        if (statsResponse.done && statsResponse.data) {
          const { tabs, sub_tabs } = statsResponse.data;
          // Preserve the missingMapping value owned by the mapping-count call
          // (or its previous value) — /stats doesn't supply that number.
          setTabCounts((prev) => transformTabCounts(tabs, sub_tabs, prev.missingMapping));
          setSubTabCounts(transformSubTabCounts(sub_tabs));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch data review stats:', error);
        toast.error('Failed to load tab counts');
        // Keep default/previous counts on error
      })
      .finally(() => {
        setIsLoadingStats(false);
      });

    // Independent of the tab counts: only updates the Missing Mapping badge.
    getIncompleteMappingCount(channelKey)
      .then((mappingCountResponse) => {
        if (mappingCountResponse.done) {
          setTabCounts((prev) => ({
            ...prev,
            missingMapping: mappingCountResponse.data.incomplete_count,
          }));
        }
      })
      .catch((error) => {
        console.error('Failed to fetch incomplete mapping count:', error);
        // Leave the Missing Mapping badge at its previous value on error
      });
  }, [channelKey]);

  // Redirect to permission denied page if user lacks read permission
  useEffect(() => {
    if (!readPermission) {
      navigate("/workspace/module/permission/denied");
    }
  }, [readPermission, navigate]);

  // Fetch stats when channel key changes
  useEffect(() => {
    if (!readPermission) return;
    if (channelKey) {
      fetchStats();
    }
  }, [channelKey, fetchStats, readPermission]);

  // Reset editInProgress and clear handlers on unmount (e.g., navigating away)
  useEffect(() => {
    return () => {
      appContext?.setEditInProgress(false);
      appContext?.setEditSaveHandler(null);
      appContext?.setEditCancelHandler(null);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh tab counts every 1 minute
  useEffect(() => {
    if (!readPermission || !channelKey) return;
    const interval = setInterval(() => {
      fetchStats();
    }, 60000);
    return () => clearInterval(interval);
  }, [channelKey, fetchStats, readPermission]);

  /**
   * Refresh stats after data changes (e.g., after bulk actions)
   */
  const refreshStats = () => {
    fetchStats();
  };

  // Build tab items with badge counts from design system Tab component
  // Show loading indicator (undefined badge) while stats are being fetched
  const tabs: TabItem[] = TAB_CONFIG.map((tab) => {
    const countMap: Record<TabId, number> = {
      'sku-matched': tabCounts.skuMatched,
      'mpn-upc-matched': tabCounts.mpnUpcMatched,
      'unmatched': tabCounts.unmatched,
      'ignored-products': tabCounts.ignoredProducts,
      'ignored-updates': tabCounts.ignoredUpdates,
      'processed': tabCounts.processed,
    };

    return {
      id: tab.id,
      label: tab.label,
      badge: isLoadingStats ? undefined : countMap[tab.id],
    };
  });

  const hasHandlers = editSaveRef.current && editCancelRef.current;

  const handleTabChange = (tabId: string | number) => {
    if (isEditMode) {
      if (hasHandlers) {
        setPendingTabId(tabId as TabId);
        setShowUnsavedModal(true);
      } else {
        toast.warning('Please save or cancel your edits before switching tabs');
      }
      return;
    }
    setActiveTabId(tabId as TabId);
  };

  const handleMissingMappingClick = () => {
    if (isEditMode) {
      if (hasHandlers) {
        setPendingMissingMapping(true);
        setShowUnsavedModal(true);
      } else {
        toast.warning('Please save or cancel your edits before switching views');
      }
      return;
    }
    setShowMissingMapping(true);
  };

  const handleModalSave = async () => {
    if (!editSaveRef.current) return;
    setSaveInProgress(true);
    try {
      const success = await editSaveRef.current();
      if (success) {
        setShowUnsavedModal(false);
        if (pendingTabId) {
          setActiveTabId(pendingTabId);
          setPendingTabId(null);
        }
        if (pendingMissingMapping) {
          setShowMissingMapping(true);
          setPendingMissingMapping(false);
        }
      }
    } finally {
      setSaveInProgress(false);
    }
  };

  const handleModalDiscard = () => {
    editCancelRef.current?.();
    setShowUnsavedModal(false);
    if (pendingTabId) {
      setActiveTabId(pendingTabId);
      setPendingTabId(null);
    }
    if (pendingMissingMapping) {
      setShowMissingMapping(true);
      setPendingMissingMapping(false);
    }
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);
    setPendingTabId(null);
    setPendingMissingMapping(false);
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    if (showMissingMapping) {
      return <MissingMappingView onRefresh={refreshStats} />;
    }

    // Common props to pass to views
    const viewProps = {
      channelKey: channelKey || '',
      onRefresh: refreshStats,
      hasUpdatePermission: !!updatePermission,
      // Consumed by SKUMatchedView ("Update All") and UnmatchedView
      // ("Create All"); the other views ignore it.
      hasBulkUpdatePermission: !!bulkUpdatePermission,
    };

    switch (activeTabId) {
      case 'sku-matched':
        return <SKUMatchedView {...viewProps} onEditModeChange={handleEditModeChange} onRegisterEditHandlers={onRegisterEditHandlers} />;
      case 'mpn-upc-matched':
        return (
          <MPNUPCMatchedView
            {...viewProps}
            subTabCounts={subTabCounts}
            onEditModeChange={handleEditModeChange}
            onRegisterEditHandlers={onRegisterEditHandlers}
          />
        );
      case 'unmatched':
        return <UnmatchedView {...viewProps} onEditModeChange={handleEditModeChange} onRegisterEditHandlers={onRegisterEditHandlers} />;
      case 'ignored-products':
        return <IgnoredProductsView {...viewProps} />;
      case 'ignored-updates':
        return <IgnoredUpdatesView {...viewProps} />;
      case 'processed':
        return <ProcessedView {...viewProps} />;
      default:
        return null;
    }
  };

  return (
    <div id="data-review-page" data-testid="catalog-management-page" className="tw-max-w-full tw-relative">
      {/* Missing Mapping Page Header - shown when Missing Mapping view is active */}
      {showMissingMapping && (
        /* Role tokens (2026-09-09, user screenshot in dark): the rule was `grey-200` —
           the LIGHT hairline literal, a bright white line on the dark ladder — and the
           back arrow `grey-700` on a `grey-100` hover: navy on navy. The arrow is a DS
           IconButton now (§ 212): subtle variant, sm square, focus ring, themed inks;
           testid and accessible name unchanged. (A JS comment, not a JSX one — this sits
           inside the `&&` expression, before the element.) */
        <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-1.5 tw-mb-4 tw-border-b tw-border-edge">
          <IconButton
            onClick={() => setShowMissingMapping(false)}
            data-testid="catalog-management-back-btn"
            aria-label="Back"
            variant="subtle"
            size="sm"
          >
            <ArrowLeft size={20} aria-hidden="true" />
          </IconButton>
          {/* `as="h1"`, matching the default header below — this replaces it rather
              than nesting under it, so the two are never on screen together.
              `heading-2` defaults to <h3>, so this view's ONLY heading used to be an
              <h3> with no <h1> or <h2> above it: a screen-reader user navigating by
              heading landed two levels down with nothing to anchor to. The type token
              was already right; only the element changed. */}
          <Typography
            as="h1"
            variant="display"
            className="tw-text-ink"
            data-testid="catalog-missing-mapping-title"
          >
            Missing Mapping
          </Typography>
        </div>
      )}

      {/* Default Page Header - shown when Missing Mapping view is NOT active */}
      {!showMissingMapping && (
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
          {/*
            `heading-2` + `as="h1"` — the page-title pairing this app settled on in
            §§ 131 and 134, and what **16 of the 19** `as="h1"` titles in `src/` use
            (`ISRHomePage`, `RuleView`, `ListView`/`Edit`/`Details`, `ManageParty`,
            `ConnectionsHome`, Admin Settings, the three AB Testing headers, Product
            detail…). 18px/500: the SIZE carries the hierarchy, the weight does not
            need to.

            Two separate faults were here, and the first fix only caught one:

            - **No `as`.** `heading-1` defaults to `<h2>`, so this page shipped with
              no `<h1>` at all.
            - **`heading-1`, which is 18px/700.** Same size as `heading-2`, heavier
              weight — so "Data Review" rendered bold while every neighbouring page
              title rendered medium. Corrected 2026-08-31 after a user put the two
              side by side.

            I picked `heading-1` by counting `variant="heading-1"` across `src/` (40
            hits) without pairing it with `as="h1"` — and most of those 40 are
            `DesignSystemShowcase` demonstrating the token. Counting a token's
            occurrences is not the same as finding a convention; the query has to name
            the role. `pageTitleConvention.test.tsx` now asserts this so the next
            person does not have to know.

            `tw-text-ink` rather than `tw-text-ink` — the same #172b4d, but the
            role layer instead of the palette layer.
          */}
          <Typography
            as="h1"
            variant="display"
            className="tw-text-ink"
            data-testid="catalog-management-title"
          >
            {PAGE_TITLE}
          </Typography>

          <div className="tw-flex tw-items-center tw-gap-3">
            {/* Missing Mapping Button */}
            <Button
              variant="tertiary"
              showBadge={true}
              badgeContent={String(tabCounts.missingMapping).padStart(2, '0')}
              onClick={handleMissingMappingClick}
              disabled={tabCounts.missingMapping === 0}
              data-testid="catalog-management-missing-mapping-btn"
            >
              Missing Mapping
            </Button>

            {/* Review Queue Pill (tickets 11446 + 11447) */}
            <Button
              variant="tertiary"
              showBadge={true}
              badgeContent={String(reviewQueue.pendingCount).padStart(2, '0')}
              onClick={() => {
                setReviewQueueOpen(true);
                reviewQueue.refresh();
              }}
              data-testid="catalog-management-review-queue-btn"
            >
              Review Queue
            </Button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      {!showMissingMapping && (
        <Tab
          tabs={tabs}
          activeTabId={activeTabId}
          onChange={handleTabChange}
          showIcons={false}
        />
      )}

      {/* Tab Content */}
      <div className="tw-mt-4">
        {renderTabContent()}
      </div>

      {/* Unsaved Changes Modal for tab/view switching */}
      {showUnsavedModal && (
        <UnsavedChangesModal
          id="data-review-unsaved-changes-modal"
          title="Unsaved Changes"
          message="You have unsaved changes. Do you want to save them before proceeding?"
          confirmText="Save Changes"
          cancelText="Cancel"
          discardText="Discard changes"
          onClose={handleModalCancel}
          onConfirm={handleModalSave}
          onDiscard={handleModalDiscard}
          saveInProgress={saveInProgress}
        />
      )}

      {/* Review Queue Drawer (tickets 11446 + 11447) */}
      <ReviewQueueDrawer
        isOpen={reviewQueueOpen}
        onClose={() => setReviewQueueOpen(false)}
        entries={reviewQueue.entries}
        loading={reviewQueue.loading}
        loadingMore={reviewQueue.loadingMore}
        hasMore={reviewQueue.hasMore}
        error={reviewQueue.error}
        onRefresh={reviewQueue.refresh}
        onLoadMore={reviewQueue.loadMore}
        onRefreshEntry={reviewQueue.refreshEntry}
        onRetry={reviewQueue.retry}
      />
    </div>
  );
};

export default CatalogManagement;
